import React, { useState } from "react"
import webify from "../util/webify"

type AchIncentiveType = "card_fee" | "ach_discount" | "none"
type SelectionType = "custom" | "default"

interface TripConvenienceFeeFieldsProps {
  typeInputName: string
  percentageInputName: string
  tenantAchIncentiveType: AchIncentiveType
  tenantAchIncentivePercentage: number
  tripAchIncentiveType: AchIncentiveType
  tripAchIncentivePercentage: number
  tripName: string
  tenantName: string
}

function initialSelectionType(props: TripConvenienceFeeFieldsProps): SelectionType {
  if (
    props.tenantAchIncentivePercentage === props.tripAchIncentivePercentage &&
    props.tenantAchIncentiveType === props.tripAchIncentiveType
  ) {
    return "default"
  } else {
    return "custom"
  }
}

function formatIncentive(incentiveType: AchIncentiveType, incentivePercentage: number): string {
  if (incentiveType === "card_fee") {
    return `${incentivePercentage}% convenience fees`
  } else if (incentiveType === "ach_discount") {
    return `${incentivePercentage}% bank transfer discounts`
  } else {
    return "no bank transfer incentive"
  }
}

const TripConvenienceFeeFields = (props: TripConvenienceFeeFieldsProps) => {
  let {
    typeInputName,
    percentageInputName,
    tenantAchIncentiveType,
    tenantAchIncentivePercentage,
    tripAchIncentiveType,
    tripAchIncentivePercentage,
    tripName,
    tenantName,
  } = props
  let [selectionType, setSelectionType] = useState(initialSelectionType(props))
  let [achIncentiveType, setAchIncentiveType] = useState(tripAchIncentiveType)
  let [achIncentivePercentage, setAchIncentivePercentage] = useState(
    tripAchIncentivePercentage.toString()
  )

  return (
    <div className="trip-convenience-fee-fields-component">
      <div className="grid gap-5">
        <div
          className="radio-list"
          onClick={() => {
            setSelectionType("default")
            setAchIncentiveType(tenantAchIncentiveType)
            setAchIncentivePercentage(tenantAchIncentivePercentage.toString())
          }}
        >
          <input
            className="form-control"
            type="radio"
            checked={selectionType === "default"}
            id="selection-type-default"
            data-role="selection-type-default"
          />
          <label htmlFor="selection-type-default">
            <h4>Account Default Settings</h4>
            <p>
              The current default setting for <span className="text-gray-800">{tenantName}</span> is{" "}
              <span className="text-gray-800">
                {formatIncentive(tenantAchIncentiveType, tenantAchIncentivePercentage)}
              </span>
            </p>
          </label>
        </div>

        <div className="radio-list" onClick={() => setSelectionType("custom")}>
          <input
            type="radio"
            checked={selectionType === "custom"}
            id="selection-type-custom"
            data-role="selection-type-custom"
          />
          <label htmlFor="selection-type-custom">
            <h4>Custom Trip Setting</h4>
            <p>
              This custom setting only affects <span className="text-gray-800">{tripName}</span>
            </p>
          </label>
        </div>

        {selectionType == "custom" && (
          <div className="override-radiolist">
            <div className="radio-list selected" onClick={() => setAchIncentiveType("card_fee")}>
              <input
                type="radio"
                checked={achIncentiveType === "card_fee"}
                id="ach-incentive-type-card-fee"
                data-role="ach-incentive-type-card-fee"
              />
              <label htmlFor="ach-incentive-type-card-fee">
                <div className="col1">
                  <h4>Higher convenience fee for card transactions.</h4>
                  <p>Add a convenience fee to registrations for using a card. (4% max)</p>
                </div>
                {achIncentiveType === "card_fee" && (
                  <div className="col2">
                    <span className="percentage-field">
                      <input
                        className="h-8 p-2"
                        data-role="ach-incentive-percentage"
                        required
                        placeholder="3"
                        type="text"
                        value={achIncentivePercentage}
                        onChange={(e) => setAchIncentivePercentage(e.target.value)}
                      />
                    </span>
                    <span className="percentage">%</span>
                  </div>
                )}
              </label>
            </div>

            <div className="radio-list">
              <input
                type="radio"
                checked={selectionType === "custom" && achIncentiveType === "ach_discount"}
                onClick={() => setAchIncentiveType("ach_discount")}
                id="ach-incentive-type-ach-discount"
                data-role="ach-incentive-type-ach-discount"
              />
              <label htmlFor="ach-incentive-type-ach-discount">
                <div className="col1">
                  <h4>Discount for bank transfers.</h4>
                  <p>Lower the cost of a trip for registrations using bank transfers. (4% max)</p>
                </div>
                {achIncentiveType === "ach_discount" && (
                  <div className="col2">
                    <span className="percentage-field">
                      <input
                        className="h-8 p-2"
                        data-role="ach-incentive-percentage"
                        placeholder="3"
                        required
                        type="text"
                        value={achIncentivePercentage}
                        onChange={(e) => setAchIncentivePercentage(e.target.value)}
                      />
                    </span>
                    <span className="percentage">%</span>
                  </div>
                )}
              </label>
            </div>

            <div className="radio-list">
              <input
                type="radio"
                checked={achIncentiveType === "none"}
                onClick={() => setAchIncentiveType("none")}
                id="ach-incentive-none"
                data-role="ach-incentive-none"
              />
              <label htmlFor="ach-incentive-none">
                <div className="col1">
                  <h4>No Convenience Fee or Discount.</h4>
                  <p>
                    This is not recommended. If you choose this option, consider adding a fee for
                    processing in the trip price. Please consult with customer support with any
                    questions.
                  </p>
                </div>
              </label>
            </div>
          </div>
        )}
      </div>

      <input type="hidden" name={typeInputName} value={achIncentiveType} />
      <input type="hidden" name={percentageInputName} value={achIncentivePercentage} />
    </div>
  )
}

export default TripConvenienceFeeFields
webify("trip-convenience-fee-fields", TripConvenienceFeeFields)
